/* AuthContext — Infosys BPM Application Access Gate.
   State machine:
     - undefined : checking cookie via /api/auth/me on boot
     - null      : unauthenticated → show LoginPage
     - {user...} : authenticated   → render the app shell

   ── v3.9.12 — cookie-only auth ─────────────────────────────────────────
   The JWT lives exclusively in the httpOnly `infoshop_at` cookie set by
   the backend at login (Secure + SameSite=Lax). It is never readable
   from JavaScript and never touches localStorage — closing the XSS
   token-theft vector permanently. Every axios request in the app carries
   the cookie automatically via the global withCredentials default below.

   CSRF: SameSite=Lax blocks the common CSRF vectors (cross-site form
   POSTs are not sent with credentials). The app has no cross-origin
   state-changing form submissions; JSON POSTs preflight through CORS. */
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Global default: EVERY axios request in the app sends the auth cookie.
axios.defaults.withCredentials = true;

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = checking

  const apiClient = useCallback(
    (path, opts = {}) =>
      axios({
        url: `${API}${path}`,
        withCredentials: true,
        ...opts,
      }),
    []
  );

  // On boot: always hit /api/auth/me — the httpOnly cookie rides along
  // automatically. 401 → unauthenticated.
  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then(({ data }) => { if (active) setUser(data); })
      .catch(() => { if (active) setUser(null); });
    return () => { active = false; };
  }, []);

  const login = useCallback(async (username, password) => {
    // Login sets the httpOnly cookie server-side; the response body
    // carries only the public user record (+ expiry), never the token.
    const { data } = await axios.post(
      `${API}/auth/login`,
      { username, password },
      { withCredentials: true }
    );
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, null, { withCredentials: true });
    } catch (err) {
      // best-effort — cookie is cleared server-side when reachable;
      // local state resets regardless.
      console.warn("[Auth] /auth/logout failed (non-fatal):", err);
    }
    setUser(null);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, login, logout, apiClient }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
